import React from 'react';

interface OnboardingData {
  restaurantName: string;
  cuisine: string;
  address: string;
  ownerName: string;
  ownerEmail: string;
  brandVoice: {
    tone: string;
    style: string;
    signOff: string;
  };
}

interface RestaurantProfileProps {
  data: OnboardingData;
  onUpdate: (data: Partial<OnboardingData>) => void;
}

const CUISINE_OPTIONS = [
  { value: '', label: 'Select cuisine type' },
  { value: 'american', label: 'American' },
  { value: 'italian', label: 'Italian' },
  { value: 'mexican', label: 'Mexican' },
  { value: 'chinese', label: 'Chinese' },
  { value: 'japanese', label: 'Japanese' },
  { value: 'indian', label: 'Indian' },
  { value: 'thai', label: 'Thai' },
  { value: 'mediterranean', label: 'Mediterranean' },
  { value: 'french', label: 'French' },
  { value: 'other', label: 'Other' },
];

const TONE_OPTIONS = [
  { value: 'warm and professional', label: 'Warm and Professional' },
  { value: 'casual and friendly', label: 'Casual and Friendly' },
  { value: 'formal and elegant', label: 'Formal and Elegant' },
  { value: 'upbeat and energetic', label: 'Upbeat and Energetic' },
];

const STYLE_OPTIONS = [
  { value: 'conversational yet respectful', label: 'Conversational yet Respectful' },
  { value: 'straightforward and concise', label: 'Straightforward and Concise' },
  { value: 'storytelling and narrative', label: 'Storytelling and Narrative' },
  { value: 'community-focused', label: 'Community-Focused' },
];

export const RestaurantProfile: React.FC<RestaurantProfileProps> = ({ data, onUpdate }) => {
  const handleFieldChange = (field: string, value: string) => {
    onUpdate({ [field]: value });
  };

  const handleBrandVoiceChange = (field: string, value: string) => {
    onUpdate({
      brandVoice: {
        ...data.brandVoice,
        [field]: value,
      },
    });
  };

  return (
    <div>
      <h2>Restaurant Profile</h2>
      <p className="wizard-step-description">
        Tell us about your restaurant. This information will be used across the platform for
        review responses, social media, and customer communications.
      </p>

      <div className="wizard-form-group">
        <label>
          Restaurant Name <span className="required">*</span>
        </label>
        <input
          type="text"
          value={data.restaurantName}
          onChange={(e) => handleFieldChange('restaurantName', e.target.value)}
          placeholder="Enter your restaurant name"
        />
      </div>

      <div className="wizard-form-row">
        <div className="wizard-form-group">
          <label>
            Cuisine Type <span className="required">*</span>
          </label>
          <select
            value={data.cuisine}
            onChange={(e) => handleFieldChange('cuisine', e.target.value)}
          >
            {CUISINE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="wizard-form-group">
          <label>Address</label>
          <input
            type="text"
            value={data.address}
            onChange={(e) => handleFieldChange('address', e.target.value)}
            placeholder="123 Main St, City, State ZIP"
          />
        </div>
      </div>

      <div className="wizard-form-row">
        <div className="wizard-form-group">
          <label>Owner / Manager Name</label>
          <input
            type="text"
            value={data.ownerName}
            onChange={(e) => handleFieldChange('ownerName', e.target.value)}
            placeholder="Full name"
          />
        </div>

        <div className="wizard-form-group">
          <label>
            Owner / Manager Email <span className="required">*</span>
          </label>
          <input
            type="email"
            value={data.ownerEmail}
            onChange={(e) => handleFieldChange('ownerEmail', e.target.value)}
            placeholder="email@example.com"
          />
        </div>
      </div>

      {/* Brand Voice Section */}
      <div className="wizard-form-section">
        <h3>Brand Voice</h3>
        <p>
          Define how your restaurant communicates with customers. This guides AI-generated
          review responses, social media content, and marketing materials.
        </p>

        <div className="wizard-form-row">
          <div className="wizard-form-group">
            <label>Tone</label>
            <select
              value={data.brandVoice.tone}
              onChange={(e) => handleBrandVoiceChange('tone', e.target.value)}
            >
              {TONE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <small>Sets the overall emotional character of communications</small>
          </div>

          <div className="wizard-form-group">
            <label>Style</label>
            <select
              value={data.brandVoice.style}
              onChange={(e) => handleBrandVoiceChange('style', e.target.value)}
            >
              {STYLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <small>Determines the structure and approach of written content</small>
          </div>
        </div>

        <div className="wizard-form-group">
          <label>Sign-off Name</label>
          <input
            type="text"
            value={data.brandVoice.signOff}
            onChange={(e) => handleBrandVoiceChange('signOff', e.target.value)}
            placeholder="e.g., the management team"
          />
          <small>
            Used at the end of review responses and customer communications (e.g., "-- the
            management team")
          </small>
        </div>
      </div>
    </div>
  );
};

export default RestaurantProfile;
